"use client";

import React, { useMemo } from "react";
import { BaseChart } from "./base-chart";
import { resolveChartTokens } from "@/utils/chart-theme";
import { cn } from "@/src/utils/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GaugeChartProps {
  /** Current value to display */
  value: number;
  /** Minimum value in range. Default: 0 */
  min?: number;
  /** Maximum value in range. Default: 100 */
  max?: number;
  /** Chart title */
  title?: string;
  /** Chart subtitle */
  subtitle?: string;
  /** Chart height — CSS value or number (px). Default 280 */
  height?: string | number;
  /** Extra class names */
  className?: string;
  /** Label text below the value */
  label?: string;
  /** Text appended to value display (e.g., "%") */
  suffix?: string;
  /** Custom color thresholds: [{value, color}] */
  thresholds?: { value: number; color: string }[];
  /** Show loading spinner. Default: false */
  loading?: boolean;
  /** Show empty state. Default: false */
  empty?: boolean;
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

// Fallback tokens if resolveChartTokens() fails
const FALLBACK_TOKENS = {
  fontFamily: "system-ui, sans-serif",
  textColor: "#1a1a1a",
  textColorSecondary: "#888888",
  backgroundColor: "#ffffff",
  borderColor: "#e5e5e5",
  errorColor: "#ef4444",
  warningColor: "#f59e0b",
  successColor: "#22c55e",
  colors: [] as string[],
  fontSize: 12,
  infoColor: "#3b82f6",
  containerPadding: 16,
  animationDuration: 800,
  animationEasing: "cubicInOut",
};

// ---------------------------------------------------------------------------
// GaugeChart Component
// ---------------------------------------------------------------------------

export function GaugeChart({
  value,
  min = 0,
  max = 100,
  title,
  subtitle,
  height = 280,
  className,
  label,
  suffix = "",
  thresholds,
  loading = false,
  empty = false,
  ariaLabel,
}: GaugeChartProps) {
  const option = useMemo(() => {
    // Resolve tokens safely
    let tokens: typeof FALLBACK_TOKENS;
    try {
      tokens = resolveChartTokens();
    } catch {
      tokens = FALLBACK_TOKENS;
    }

    // Build threshold color array for axisLine
    const defaultThresholds = [
      { value: 0.6, color: tokens.errorColor },
      { value: 0.8, color: tokens.warningColor },
      { value: 1, color: tokens.successColor },
    ];

    const axisLineColors = thresholds
      ? thresholds.map((t) => [t.value / max, t.color] as [number, string])
      : defaultThresholds.map((t) => [t.value, t.color] as [number, string]);

    // Build option object (avoid spreading chartAnimation which adds extra keys)
    const opt: Record<string, any> = {
      animation: true,
      animationDuration: 1200,
      animationEasing: "cubicInOut",
    };

    // Title
    if (title) {
      opt.title = {
        text: title,
        subtext: subtitle || "",
        left: "center",
        top: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          fontFamily: tokens.fontFamily,
          color: tokens.textColor,
        },
        subtextStyle: {
          fontSize: 12,
          fontFamily: tokens.fontFamily,
          color: tokens.textColorSecondary,
        },
      };
    }

    // Gauge series
    opt.series = [
      {
        type: "gauge",
        startAngle: 200,
        endAngle: -20,
        min,
        max,
        center: ["50%", "60%"],
        radius: "90%",
        // Progress arc
        progress: {
          show: true,
          width: 14,
          roundCap: true,
        },
        // Background arc with threshold colors
        axisLine: {
          lineStyle: {
            width: 14,
            color: axisLineColors,
          },
          roundCap: true,
        },
        // Hide ticks and labels on the arc
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        // Pointer
        pointer: {
          show: true,
          length: "55%",
          width: 3,
          itemStyle: {
            color: tokens.textColor,
          },
        },
        // Center anchor dot
        anchor: {
          show: true,
          size: 8,
          itemStyle: {
            color: tokens.textColor,
            borderWidth: 2,
            borderColor: tokens.backgroundColor,
          },
        },
        // Label below value
        title: {
          show: !!label,
          offsetCenter: [0, "30%"],
          fontSize: 13,
          fontFamily: tokens.fontFamily,
          color: tokens.textColorSecondary,
        },
        // Value display
        detail: {
          offsetCenter: [0, "10%"],
          fontSize: 28,
          fontWeight: 700,
          fontFamily: tokens.fontFamily,
          color: tokens.textColor,
          formatter: `{value}${suffix}`,
        },
        data: [{ value, name: label || "" }],
        animationDuration: 1200,
        animationEasing: "cubicInOut",
      },
    ];

    return opt;
  }, [value, min, max, title, subtitle, label, suffix, thresholds]);

  return (
    <BaseChart
      option={option}
      height={height}
      className={cn("gauge-chart", className)}
      loading={loading}
      empty={empty}
      ariaLabel={ariaLabel || `Gauge chart${label ? `: ${label}` : ""}, value: ${value}${suffix || ""}${max ? ` of ${max}` : ""}`}
    />
  );
}

export default GaugeChart;
